// src/components/admin/AdminAnalytics.js
import React, { useState, useEffect } from 'react';
import { db } from '../../services/firebase/config';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { getIndiaDateString, getIndiaDate } from '../../services/firebase/firestoreService';

export default function AdminAnalytics() {
  const [quickStats, setQuickStats] = useState(null);
  const [quickLoading, setQuickLoading] = useState(true);
  const [atRisk, setAtRisk] = useState([]);
  const [atRiskLoading, setAtRiskLoading] = useState(true);
  const [period, setPeriod] = useState('weekly');
  const [completionRate, setCompletionRate] = useState(null);
  const [villageRankings, setVillageRankings] = useState([]);
  const [streakDist, setStreakDist] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendLoaded, setTrendLoaded] = useState(false);
  const [chartStartDate, setChartStartDate] = useState('');
  const [chartEndDate, setChartEndDate] = useState('');
  const [chartGroupBy, setChartGroupBy] = useState('village');
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartDates, setChartDates] = useState([]);
  const [cohortData, setCohortData] = useState(null);
  const [cohortLoading, setCohortLoading] = useState(false);
  const [cohortLoaded, setCohortLoaded] = useState(false);

  useEffect(() => { loadQuickStats(); loadAtRisk(); }, []);

  const loadQuickStats = async () => {
    setQuickLoading(true);
    try {
      const snap = await getDocs(collection(db, 'users'));
      const users = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(u => u.role !== 'admin');
      const active = users.filter(u => u.status !== 'inactive');
      const withNotif = active.filter(u => u.notificationPromptStatus === 'subscribed');
      const day1Completed = active.filter(u => (u.fitnessCurrentDay || 1) > 1);
      const yesterday = new Date(getIndiaDate().getTime() - 24 * 60 * 60 * 1000);
      const yesterdayStr = getIndiaDateString(yesterday);
      let yesterdayCompletions = 0;
      if (active.length > 0) {
        const results = await Promise.all(
          active.map(u => getDoc(doc(db, 'fitnessDailyCompletions', `${u.id}_${yesterdayStr}`)))
        );
        yesterdayCompletions = results.filter(d => d.exists() && d.data()?.allTasksCompleted).length;
      }
      setQuickStats({
        totalActive: active.length,
        totalUsers: users.length,
        yesterdayRate: active.length > 0 ? Math.round((yesterdayCompletions / active.length) * 100) : 0,
        notifRate: active.length > 0 ? Math.round((withNotif.length / active.length) * 100) : 0,
        day1Rate: active.length > 0 ? Math.round((day1Completed.length / active.length) * 100) : 0,
      });
    } catch (e) {
    } finally {
      setQuickLoading(false);
    }
  };

  const loadAtRisk = async () => {
    setAtRiskLoading(true);
    try {
      const snap = await getDocs(collection(db, 'users'));
      const users = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(u => u.role !== 'admin' && u.status !== 'inactive');
      const threeDaysAgo = new Date(getIndiaDate().getTime() - 3 * 24 * 60 * 60 * 1000);
      const risk = users.filter(u => {
        if (!u.lastActive) return true;
        const last = u.lastActive.seconds ? new Date(u.lastActive.seconds * 1000) : new Date(u.lastActive);
        return last < threeDaysAgo;
      }).map(u => ({
        id: u.id,
        name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || '—',
        village: u.village, phone: u.phone,
        currentDay: u.fitnessCurrentDay || 1,
        daysInactive: u.lastActive
          ? Math.floor((getIndiaDate() - (u.lastActive.seconds ? new Date(u.lastActive.seconds * 1000) : new Date(u.lastActive))) / (1000 * 60 * 60 * 24))
          : 999
      })).sort((a, b) => b.daysInactive - a.daysInactive);
      setAtRisk(risk);
    } catch (e) {
    } finally {
      setAtRiskLoading(false);
    }
  };

  const loadTrendData = async () => {
    setTrendLoading(true);
    try {
      const snap = await getDocs(collection(db, 'users'));
      const users = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(u => u.role !== 'admin' && u.status !== 'inactive');
      const days = period === 'today' ? 1 : period === 'weekly' ? 7 : 30;
      const today = getIndiaDate();
      const dates = [];
      for (let i = days - 1; i >= 0; i--) {
        const d = new Date(today.getTime() - i * 24 * 60 * 60 * 1000);
        dates.push(getIndiaDateString(d));
      }
      let totalCompleted = 0;
      for (const dateStr of dates) {
        const results = await Promise.all(
          users.map(u => getDoc(doc(db, 'fitnessDailyCompletions', `${u.id}_${dateStr}`)))
        );
        totalCompleted += results.filter(d => d.exists() && d.data()?.allTasksCompleted).length;
      }
      setCompletionRate(users.length > 0 ? Math.round((totalCompleted / (users.length * days)) * 100) : 0);

      const villageMap = {};
      for (const dateStr of dates) {
        for (const user of users) {
          if (!user.village) continue;
          if (!villageMap[user.village]) villageMap[user.village] = { total: 0, completed: 0 };
          villageMap[user.village].total++;
          const s = await getDoc(doc(db, 'fitnessDailyCompletions', `${user.id}_${dateStr}`));
          if (s.exists() && s.data()?.allTasksCompleted) villageMap[user.village].completed++;
        }
      }
      setVillageRankings(Object.entries(villageMap).map(([village, data]) => ({
        village, rate: Math.round((data.completed / data.total) * 100),
        completed: data.completed, total: data.total
      })).sort((a, b) => b.rate - a.rate));

      const dist = { '0-7': 0, '8-14': 0, '15-21': 0, '22-30': 0, '30+': 0 };
      users.forEach(u => {
        const s = u.streakCount || 0;
        if (s <= 7) dist['0-7']++;
        else if (s <= 14) dist['8-14']++;
        else if (s <= 21) dist['15-21']++;
        else if (s <= 30) dist['22-30']++;
        else dist['30+']++;
      });
      setStreakDist(dist);
      setTrendLoaded(true);
    } catch (e) {
    } finally {
      setTrendLoading(false);
    }
  };

  const loadActivityChart = async () => {
    if (!chartStartDate || !chartEndDate) return;
    setChartLoading(true);
    try {
      const snap = await getDocs(collection(db, 'users'));
      const users = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(u => u.role !== 'admin');
      const dates = [];
      const start = new Date(chartStartDate + 'T00:00:00');
      const end = new Date(chartEndDate + 'T00:00:00');
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        dates.push(getIndiaDateString(new Date(d)));
      }
      setChartDates(dates);
      const grouped = {};
      for (const user of users) {
        const batchStr = user.fitnessStartDate?.seconds
             ? getIndiaDateString(new Date(user.fitnessStartDate.seconds * 1000))
             : user.fitnessStartDate;
        const groupKey = chartGroupBy === 'village'
             ? (user.village || 'Unknown')
             : (batchStr || 'Unknown');
        if (!grouped[groupKey]) grouped[groupKey] = [];
        const completions = {};
        for (const dateStr of dates) {
          // Fix: proper date comparison for notstarted
          const batchStr = user.fitnessStartDate?.seconds
             ? getIndiaDateString(new Date(user.fitnessStartDate.seconds * 1000))
             : user.fitnessStartDate;
            if (batchStr && dateStr < batchStr) {
            completions[dateStr] = 'notstarted';
          } else {
            const s = await getDoc(doc(db, 'fitnessDailyCompletions', `${user.id}_${dateStr}`));
            completions[dateStr] = s.exists() && s.data()?.allTasksCompleted ? 'completed' : 'missed';
          }
        }
        const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || '—';
        grouped[groupKey].push({ name: fullName, fitnessStartDate: user.fitnessStartDate, completions });
      }
      setChartData(grouped);
    } catch (e) {
    } finally {
      setChartLoading(false);
    }
  };

 const loadCohortData = async () => {
    setCohortLoading(true);
    try {
      const snap = await getDocs(collection(db, 'users'));
      const users = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(u => u.role !== 'admin' && u.fitnessStartDate);

      const getBatchStr = (fitnessStartDate) => {
        if (!fitnessStartDate) return null;
        if (fitnessStartDate?.seconds) return getIndiaDateString(new Date(fitnessStartDate.seconds * 1000));
        return fitnessStartDate;
      };

      const cohorts = {};
      users.forEach(u => {
        const batchStr = getBatchStr(u.fitnessStartDate);
        if (!batchStr) return;
        const month = batchStr.slice(0, 7);
        if (!cohorts[month]) cohorts[month] = [];
        cohorts[month].push({ ...u, batchDateStr: batchStr });
      });

      const today = getIndiaDate();
      const todayStr = getIndiaDateString(today);
      const result = [];

      for (const [cohortMonth, cohortUsers] of Object.entries(cohorts).sort()) {
        const row = { cohort: cohortMonth, total: cohortUsers.length, weeks: [] };

        const earliestBatch = cohortUsers.reduce((min, u) =>
          u.batchDateStr < min ? u.batchDateStr : min, cohortUsers[0].batchDateStr);

        for (let w = 0; w < 16; w++) {
          const weekStartDate = new Date(earliestBatch + 'T00:00:00');
          weekStartDate.setDate(weekStartDate.getDate() + w * 7);
          const weekStartStr = getIndiaDateString(weekStartDate);

          if (weekStartStr > todayStr) { row.weeks.push(null); continue; }

          const weekEndDate = new Date(weekStartDate);
          weekEndDate.setDate(weekEndDate.getDate() + 6);
          const weekEndStr = getIndiaDateString(weekEndDate) < todayStr
            ? getIndiaDateString(weekEndDate) : todayStr;

          const weekDates = [];
          for (let d = new Date(weekStartDate);
            getIndiaDateString(new Date(d)) <= weekEndStr;
            d.setDate(d.getDate() + 1)) {
            weekDates.push(getIndiaDateString(new Date(d)));
          }

          let activeCount = 0;
          for (const user of cohortUsers) {
            for (const dateStr of weekDates) {
              const s = await getDoc(doc(db, 'fitnessDailyCompletions', `${user.id}_${dateStr}`));
              if (s.exists() && s.data()?.allTasksCompleted) { activeCount++; break; }
            }
          }
          row.weeks.push(Math.round((activeCount / cohortUsers.length) * 100));
        }
        result.push(row);
      }
      setCohortData(result);
      setCohortLoaded(true);
    } catch (e) {
    } finally {
      setCohortLoading(false);
    }
  };

  const getDayLabel = (dateStr) => {
    const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    return days[new Date(dateStr + 'T00:00:00').getDay()];
  };

  const isWeekend = (dateStr) => {
    const day = new Date(dateStr + 'T00:00:00').getDay();
    return day === 0 || day === 6;
  };

  const getCellColor = (status, dateStr) => {
    if (status === 'notstarted') return { bg: '#D3D1C7', opacity: 0.4 };
    if (status === 'completed') return { bg: '#1D9E75', opacity: 1 };
    if (status === 'missed') {
       return { bg: '#E24B4A', opacity: 0.85 };
    }
    return { bg: '#D3D1C7', opacity: 0.4 };
  };

  const getCohortColor = (pct) => {
    if (pct === null) return '#f0f0f0';
    if (pct >= 70) return '#1D9E75';
    if (pct >= 40) return '#EF9F27';
    return '#E24B4A';
  };

  return (
    <div>
      <div className="admin-section-title">Analytics</div>

      {/* QUICK STATS */}
      <div style={{ marginBottom: '8px', fontSize: '12px', color: '#28a745', fontWeight: 500 }}>
        Auto-loaded — minimal Firebase reads
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '24px' }}>
        {[
          { label: 'Active Users', value: quickLoading ? '...' : `${quickStats?.totalActive} / ${quickStats?.totalUsers}`, sub: 'active / total' },
          { label: "Yesterday's Completion", value: quickLoading ? '...' : `${quickStats?.yesterdayRate}%`, sub: 'active users only' },
          { label: 'Notification Opt-in', value: quickLoading ? '...' : `${quickStats?.notifRate}%`, sub: 'of active users' },
          { label: 'Day 1 Completion', value: quickLoading ? '...' : `${quickStats?.day1Rate}%`, sub: 'ever completed Day 1' },
        ].map((stat, i) => (
          <div key={i} className="admin-stat-card">
            <div className="admin-stat-number" style={{ fontSize: '24px' }}>{stat.value}</div>
            <div className="admin-stat-label">{stat.label}</div>
            <div style={{ fontSize: '11px', color: '#aaa', marginTop: '2px' }}>{stat.sub}</div>
          </div>
        ))}
      </div>

      {/* AT-RISK */}
      <div className="admin-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0 }}>At-Risk Users ({atRiskLoading ? '...' : atRisk.length})</h3>
          <span style={{ fontSize: '12px', color: '#28a745' }}>Auto-loaded</span>
        </div>
        <p style={{ fontSize: '13px', color: '#888', marginBottom: '12px' }}>
          Active users with no training in 3+ days.
        </p>
        {atRiskLoading ? <p>लोड होत आहे...</p> : atRisk.length === 0 ? (
          <p style={{ color: '#28a745', fontSize: '14px' }}>No at-risk users right now.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr><th>Name</th><th>Village</th><th>Phone</th><th>Day</th><th>Inactive</th></tr>
              </thead>
              <tbody>
                {atRisk.map(u => (
                  <tr key={u.id}>
                    <td style={{ fontWeight: 500 }}>{u.name}</td>
                    <td>{u.village}</td>
                    <td style={{ color: '#2563eb' }}>{u.phone}</td>
                    <td>Day {u.currentDay}</td>
                    <td style={{ color: u.daysInactive > 7 ? '#dc3545' : '#fd7e14', fontWeight: 600 }}>
                      {u.daysInactive === 999 ? 'Never' : `${u.daysInactive}d`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* COMPLETION + VILLAGE + STREAK */}
      <div className="admin-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
          <h3 style={{ margin: 0 }}>Completion, Villages & Streaks</h3>
          <span style={{ fontSize: '12px', color: '#fd7e14' }}>Requires Firebase reads</span>
        </div>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {['today', 'weekly', 'monthly'].map(p => (
            <button key={p} className={`admin-btn ${period === p ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
              style={{ padding: '6px 14px', fontSize: '13px' }}
              onClick={() => { setPeriod(p); setTrendLoaded(false); }}>
              {p === 'today' ? 'Today' : p === 'weekly' ? 'Last 7 Days' : 'Last 30 Days'}
            </button>
          ))}
          <button className="admin-btn admin-btn-secondary" style={{ padding: '6px 14px', fontSize: '13px' }}
            onClick={loadTrendData} disabled={trendLoading}>
            {trendLoading ? 'Loading...' : trendLoaded ? 'Refresh' : 'Load Data'}
          </button>
        </div>

        {!trendLoaded && !trendLoading && (
          <p style={{ color: '#999', fontSize: '14px' }}>Click "Load Data" to fetch stats.</p>
        )}

        {trendLoaded && (
          <div>
            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '13px', color: '#888', marginBottom: '4px' }}>Overall Completion Rate</div>
              <div style={{ fontSize: '36px', fontWeight: 700,
                color: completionRate >= 60 ? '#1D9E75' : completionRate >= 40 ? '#EF9F27' : '#E24B4A' }}>
                {completionRate}%
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>Village Rankings</div>
              {villageRankings.map((v, i) => (
                <div key={v.village} style={{ display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <span style={{ minWidth: '24px', fontWeight: 700,
                    color: i === 0 ? '#EF9F27' : i === 1 ? '#888' : i === 2 ? '#CD7F32' : '#555' }}>
                    #{i + 1}
                  </span>
                  <span style={{ flex: 1, fontSize: '14px' }}>{v.village}</span>
                  <span style={{ fontSize: '13px', color: '#888' }}>{v.completed}/{v.total}</span>
                  <div style={{ width: '80px', height: '8px', background: '#f0f0f0', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ width: `${v.rate}%`, height: '100%', borderRadius: '4px',
                      background: v.rate >= 60 ? '#1D9E75' : v.rate >= 40 ? '#EF9F27' : '#E24B4A' }} />
                  </div>
                  <span style={{ minWidth: '36px', fontSize: '13px', fontWeight: 600,
                    color: v.rate >= 60 ? '#1D9E75' : v.rate >= 40 ? '#EF9F27' : '#E24B4A' }}>
                    {v.rate}%
                  </span>
                </div>
              ))}
            </div>

            <div>
              <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>Streak Distribution</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                {streakDist && Object.entries(streakDist).map(([range, count]) => (
                  <div key={range} style={{ textAlign: 'center', padding: '12px 8px',
                    background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                    <div style={{ fontSize: '22px', fontWeight: 700, color: '#2563eb' }}>{count}</div>
                    <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>{range} days</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ACTIVITY HEATMAP */}
      <div className="admin-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
          <h3 style={{ margin: 0 }}>User Activity Chart</h3>
          <span style={{ fontSize: '12px', color: '#dc3545' }}>Heavy reads — use short ranges</span>
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px', alignItems: 'flex-end' }}>
          <div className="admin-form-group">
            <label>From</label>
            <input type="date" value={chartStartDate} onChange={e => setChartStartDate(e.target.value)} />
          </div>
          <div className="admin-form-group">
            <label>To</label>
            <input type="date" value={chartEndDate} onChange={e => setChartEndDate(e.target.value)} />
          </div>
          <div className="admin-form-group">
            <label>Group by</label>
            <select value={chartGroupBy} onChange={e => setChartGroupBy(e.target.value)}>
              <option value="village">Village</option>
              <option value="batchStartDate">Batch Start Date</option>
            </select>
          </div>
          <button className="admin-btn admin-btn-secondary" style={{ padding: '10px 16px' }}
            onClick={loadActivityChart}
            disabled={chartLoading || !chartStartDate || !chartEndDate}>
            {chartLoading ? 'Loading...' : 'Load Chart'}
          </button>
        </div>

        {!chartData && !chartLoading && (
          <p style={{ color: '#999', fontSize: '14px' }}>Select a date range and click "Load Chart".</p>
        )}

        {chartData && (
          <div>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', fontSize: '12px', color: '#666', flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: '#1D9E75', display: 'inline-block' }}></span>Completed
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: '#E24B4A', display: 'inline-block' }}></span>Missed (weekday)
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: '#E24B4A', opacity: 0.3, display: 'inline-block' }}></span>Missed (weekend)
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: '#D3D1C7', display: 'inline-block' }}></span>Not started
              </span>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: `${120 + chartDates.length * 14}px` }}>
                <div style={{ display: 'grid', gridTemplateColumns: `120px repeat(${chartDates.length}, 12px)`, gap: '1px', marginBottom: '4px' }}>
                  <div></div>
                  {chartDates.map((dateStr, i) => (
                    <div key={i} style={{ height: '14px', fontSize: '8px', textAlign: 'center',
                      color: isWeekend(dateStr) ? '#E24B4A' : '#888', fontWeight: 500 }}>
                      {getDayLabel(dateStr)}
                    </div>
                  ))}
                </div>

                {Object.entries(chartData).sort().map(([group, users]) => (
                  <div key={group}>
                    <div style={{ display: 'grid', gridTemplateColumns: `120px repeat(${chartDates.length}, 12px)`,
                      gap: '1px', marginTop: '10px', marginBottom: '3px' }}>
                      <div style={{ fontSize: '10px', color: '#2563eb', padding: '2px 4px', fontWeight: 600 }}>{group}</div>
                      {chartDates.map((_, i) => <div key={i}></div>)}
                    </div>
                    {users.map((user, ui) => (
                      <div key={ui} style={{ display: 'grid',
                        gridTemplateColumns: `120px repeat(${chartDates.length}, 12px)`,
                        gap: '1px', marginBottom: '2px' }}>
                        <div style={{ fontSize: '10px', color: '#333', padding: '1px 4px',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {user.name}
                        </div>
                        {chartDates.map((dateStr, i) => {
                          const { bg, opacity } = getCellColor(user.completions[dateStr], dateStr);
                          return (
                            <div key={i} style={{ height: '12px', background: bg,
                              borderRadius: '1px', opacity }} />
                          );
                        })}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* COHORT RETENTION */}
      <div className="admin-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
          <h3 style={{ margin: 0 }}>Cohort Retention</h3>
          <span style={{ fontSize: '12px', color: '#dc3545' }}>Very heavy reads — use sparingly</span>
        </div>
        <p style={{ fontSize: '13px', color: '#888', marginBottom: '16px' }}>
          % of each monthly batch still active week by week. Green = 70%+, Orange = 40-70%, Red = below 40%.
        </p>
        <button className="admin-btn admin-btn-secondary" onClick={loadCohortData} disabled={cohortLoading}>
          {cohortLoading ? 'Loading (may take a while)...' : cohortLoaded ? 'Refresh' : 'Load Cohort Data'}
        </button>

        {cohortLoaded && cohortData && cohortData.length === 0 && (
          <p style={{ color: '#999', fontSize: '14px', marginTop: '12px' }}>
            No cohort data found. Make sure users have fitnessStartDate set.
          </p>
        )}

        {cohortLoaded && cohortData && cohortData.length > 0 && (
          <div style={{ overflowX: 'auto', marginTop: '16px' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Batch</th>
                  <th>Users</th>
                  {Array.from({ length: 16 }, (_, i) => (
                    <th key={i} style={{ textAlign: 'center', fontSize: '11px', minWidth: '36px' }}>W{i + 1}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cohortData.map(row => (
                  <tr key={row.cohort}>
                    <td style={{ fontWeight: 500, fontSize: '13px' }}>{row.cohort}</td>
                    <td style={{ fontSize: '13px' }}>{row.total}</td>
                    {row.weeks.map((pct, i) => (
                      <td key={i} style={{ textAlign: 'center', padding: '3px' }}>
                        {pct === null ? (
                          <span style={{ color: '#ddd', fontSize: '11px' }}>—</span>
                        ) : (
                          <div style={{ background: getCohortColor(pct), borderRadius: '4px',
                            padding: '2px 4px', fontSize: '11px', fontWeight: 600, color: '#fff' }}>
                            {pct}%
                          </div>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}